import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSubscription } from "@/hooks/useSubscription";
import { useProfile } from "@/hooks/useProfile";
import { pushSubscriptionStarted, pushUserProperties, getAccountType, getPaidTier } from "@/lib/analytics";

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const { 
    checkSubscription, 
    tier, 
    isLoading,
    subscribed,
    subscriptionId,
    currency,
    billingCycle,
    amount 
  } = useSubscription();
  const { profile, isLoading: profileLoading, markSubscriptionTracked } = useProfile();
  const hasTrackedSubscription = useRef(false);

  // Refresh subscription status on mount
  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  // Fire subscription_started event after backend confirmation
  useEffect(() => {
    // Wait for data to load
    if (isLoading || profileLoading) return;
    
    // Must have an active subscription with all required data
    if (!subscribed || !subscriptionId || !tier || tier === 'free') return;
    if (!billingCycle || !currency || amount === null) return;
    
    // Check if already tracked this subscription (deduplication)
    if (profile?.last_subscription_id_tracked === subscriptionId) return;
    if (hasTrackedSubscription.current) return;

    hasTrackedSubscription.current = true;

    // Push user properties FIRST (per spec: must be set before/with the event)
    pushUserProperties({
      account_type: getAccountType(tier),
      paid_tier: getPaidTier(tier),
    });

    // Then fire subscription_started conversion event
    pushSubscriptionStarted({
      plan: tier as "starter" | "pro" | "unlimited",
      billing_cycle: billingCycle,
      currency: currency,
      value: amount,
      subscription_id: subscriptionId,
    });

    // Mark as tracked to prevent duplicate fires
    markSubscriptionTracked(subscriptionId);
  }, [isLoading, profileLoading, subscribed, subscriptionId, tier, billingCycle, currency, amount, profile, markSubscriptionTracked]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="max-w-md w-full mx-auto px-6 py-12 text-center">
        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-accent/10 mb-6">
            <CheckCircle2 className="h-10 w-10 text-accent" />
          </div>
          
          <h1 className="font-display text-3xl font-bold text-foreground mb-4">
            Welcome to GreenLight {!isLoading && tier !== 'free' ? tier.charAt(0).toUpperCase() + tier.slice(1) : ''}!
          </h1>
          
          <p className="text-muted-foreground text-lg">
            Your subscription is now active. You can start creating more projects right away.
          </p>
        </div>

        <div className="space-y-4">
          <Button 
            className="w-full gap-2" 
            size="lg"
            onClick={() => navigate('/dashboard')}
          >
            Go to Dashboard
            <ArrowRight className="h-4 w-4" />
          </Button>
          
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => navigate('/plans')}
          >
            View Plan Details
          </Button>
        </div>

        <p className="mt-8 text-sm text-muted-foreground">
          You'll receive a confirmation email shortly. You can manage your subscription anytime from your dashboard.
        </p>
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
